import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { FiX, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi'
import { useTranslation } from '../i18n/LanguageContext'
import './PricingExplanationPopup.css'
import { bpmTariffSummary } from '../lib/bpmNotes.js'

function BpmExplanationPopup({ isOpen, onClose, car }) {
    const { t } = useTranslation()
    const closeButtonRef = useRef(null)

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose()
        }
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            closeButtonRef.current?.focus()
            document.body.style.overflow = 'hidden'
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = ''
        }
    }, [isOpen, onClose])

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose()
    }

    if (!isOpen || !car) return null

    const fmt = (v) => v != null && !isNaN(v) ? `\u20ac${Math.round(v).toLocaleString('nl-NL')}` : 'N/A'
    const co2Source = car.bpm_co2_source || 'unknown'
    const co2Original = car.bpm_co2_original
    const co2Final = car.co2 || 0
    const bpmGross = car.bpm_gross || 0
    const bpmDeprPct = car.bpm_depr_pct || 0
    const bpmRest = car.bpm_cost || 0
    const isImputed = co2Source !== 'original'
    const isGuardrail = co2Source.includes('guardrail') || co2Source.includes('override')

    // Parse the pipe-separated notes into sections
    const notes = car.bpm_calculation_notes || ''
    const sections = notes.split(' | ').reduce((acc, part) => {
        const [key, ...rest] = part.split(': ')
        if (key && rest.length) acc[key.trim()] = rest.join(': ').trim()
        return acc
    }, {})

    const { tariff, isEstimate } = bpmTariffSummary(notes)

    // Determine CO2 confidence indicator
    let co2ConfLabel, co2ConfClass
    if (co2Source === 'original') {
        co2ConfLabel = t('Scraped from listing')
        co2ConfClass = 'bpm-conf-high'
    } else if (co2Source.includes('model+fuel+year')) {
        co2ConfLabel = t('Estimated from similar cars (same model, fuel, year)')
        co2ConfClass = 'bpm-conf-medium'
    } else if (co2Source.includes('model+fuel')) {
        co2ConfLabel = t('Estimated from similar cars (same model, fuel)')
        co2ConfClass = 'bpm-conf-medium'
    } else if (co2Source.includes('fuel+year')) {
        co2ConfLabel = t('Estimated from fuel type + year average')
        co2ConfClass = 'bpm-conf-low'
    } else if (co2Source.includes('forfaitaire')) {
        co2ConfLabel = t('Legal default (forfaitaire) - no comparable data')
        co2ConfClass = 'bpm-conf-low'
    } else {
        co2ConfLabel = co2Source
        co2ConfClass = 'bpm-conf-low'
    }

    return createPortal(
        <div className="pe-popup-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true">
            <div className="pe-popup-container" style={{ maxWidth: '460px' }}>
                <div className="pe-popup-header">
                    <h3>{car.make} {car.model} - {t('BPM Calculation')}</h3>
                    <button ref={closeButtonRef} className="pe-close-btn" onClick={onClose} aria-label={t('Close')}>
                        <FiX />
                    </button>
                </div>

                <div className="pe-popup-content">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                        {/* BPM Result */}
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{t('BPM Rest (you pay)')}</span>
                                <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#e11d48' }}>{fmt(bpmRest)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{t('Gross BPM (new car)')}</span>
                                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{fmt(bpmGross)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{t('Depreciation applied')}</span>
                                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{bpmDeprPct.toFixed(1)}%</span>
                            </div>
                        </div>

                        {/* CO2 Input */}
                        <div>
                            <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#0f172a' }}>{t('CO2 Emissions')}</h4>
                            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{t('CO2 used')}</span>
                                    <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{co2Final} g/km</span>
                                </div>
                                {isImputed && co2Original != null && co2Original > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{t('Original scraped value')}</span>
                                        <span style={{ color: '#f59e0b', fontSize: '0.8rem', textDecoration: 'line-through' }}>{Math.round(co2Original)} g/km</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '0.5rem' }}>
                                    {isGuardrail ? (
                                        <FiAlertTriangle style={{ color: '#f59e0b', flexShrink: 0 }} size={14} />
                                    ) : co2Source === 'original' ? (
                                        <FiCheckCircle style={{ color: '#22c55e', flexShrink: 0 }} size={14} />
                                    ) : (
                                        <FiAlertTriangle style={{ color: '#f59e0b', flexShrink: 0 }} size={14} />
                                    )}
                                    <span className={co2ConfClass} style={{ fontSize: '0.8rem' }}>
                                        {co2ConfLabel}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Registration Date */}
                        <div>
                            <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#0f172a' }}>{t('Depreciation')}</h4>
                            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#64748b' }}>
                                {sections['Reg'] && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>{t('Registration date')}</span>
                                        <span style={{ color: '#0f172a' }}>{sections['Reg'].split(' (')[0]}</span>
                                    </div>
                                )}
                                {sections['Reg'] && sections['Reg'].includes('(') && (
                                    <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#94a3b8' }}>
                                        {t('Source')}: {sections['Reg'].match(/\(([^)]+)\)/)?.[1] || t('Unknown')}
                                    </div>
                                )}
                                {sections['Depr'] && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                                        <span>{t('Age / depreciation')}</span>
                                        <span style={{ color: '#0f172a' }}>{sections['Depr']}</span>
                                    </div>
                                )}
                                {sections['Diesel surcharge'] && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                                        <span>{t('Diesel surcharge')}</span>
                                        <span style={{ color: sections['Diesel surcharge'] === 'yes' ? '#e11d48' : '#22c55e' }}>
                                            {sections['Diesel surcharge'] === 'yes' ? t('Yes') : t('No')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {isEstimate && (
                            <div role="note" style={{ display: 'flex', gap: '6px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: '#92400e' }}>
                                <FiAlertTriangle style={{ flexShrink: 0, marginTop: '2px' }} size={14} />
                                <span>{t('Estimate: the exact tariff needs this car\u2019s CoC (CO2 test cycle and first-registration date). This amount uses the current table and is not below the legal minimum for this CO2 value.')}</span>
                            </div>
                        )}

                        {/* Methodology */}
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', lineHeight: 1.5 }}>
                            {tariff
                                ? t('Tariff used: {tariff}, with the forfaitaire depreciation table.', { tariff })
                                : t('BPM calculated with the official Belastingdienst WLTP bands of the current year and the forfaitaire depreciation table.')}
                            {car.fuel === 'diesel' || (car.fuel && car.fuel.toLowerCase().includes('diesel'))
                                ? ' ' + t('Diesel surcharge applied for CO2 above 70 g/km.')
                                : ''}
                        </div>

                        {/* In practice tip */}
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                            <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', color: '#334155' }}>{t('In practice you often pay less')}</h4>
                            <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.8rem', color: '#475569', lineHeight: 1.6 }}>
                                <li>{t('This uses the forfaitaire table - the standard depreciation')}</li>
                                <li>{t('A taxatierapport (~{price}) typically reduces BPM by 20-40%', { price: '\u20ac150' })}</li>
                                <li>{t('Biggest savings on older, high-mileage, or premium cars')}</li>
                            </ul>
                            <a
                                href="https://www.belastingdienst.nl/wps/wcm/connect/bldcontentnl/belastingdienst/prive/auto_en_vervoer/belastingen_op_auto_en_motor/bpm/afschrijving_met_koerslijst_taxatierapport_of_forfaitaire_tabel/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'inline-block', marginTop: '0.4rem', fontSize: '0.75rem', color: '#3b82f6' }}
                            >
                                {t('Belastingdienst: afschrijvingsmethoden')} &rarr;
                            </a>
                        </div>
                    </div>
                </div>

                <div className="pe-popup-footer">
                    <button className="pe-done-btn" onClick={onClose}>{t('Got it')}</button>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default BpmExplanationPopup
